import { Hospital } from 'ui/mobile-ds/icons';
import { IClaimDetails } from '../../../types';
import { ComponentProps } from 'react';
import clsx from 'clsx';
import { Radio } from 'ui/icons';
import { INSURER_LOGOS, CARE_TYPE_CONFIG } from '@/constants';
import capitalize from 'lodash.capitalize';
import { format } from 'date-fns';
import { getPatientDetails } from '@/utils';

type IProps = {
  isSelectable?: boolean;
  isSelected: boolean;
  claimDetails: IClaimDetails;
  source: 'plum' | 'insurer' | null;
  totalClaims?: number;
};

const ImportedClaimsCard = (props: IProps & ComponentProps<'input'>) => {
  const { isSelectable, isSelected, claimDetails, totalClaims, ...rest } =
    props;

  const {
    insuranceProviderHandle,
    insurerClaimId,
    plumClaimId,
    stage,
    dateOfAdmission,
    typeOfCare,
    hospital: { hospitalname } = {},
  } = claimDetails;

  const { icon: Icon, fullName } = getPatientDetails({
    gender: claimDetails.patientGender,
    firstName: claimDetails.patientName,
    relationship: claimDetails.patientRelationship,
    memberId: claimDetails.memberId,
    dependentId:
      claimDetails.memberId !== claimDetails.patientId
        ? claimDetails.patientId
        : null,
  });

  const card = (
    <div
      className={clsx({
        'p-4 md:px-6 md:py-5 md:min-w-[400px] h-full max-h-fit rounded-2xl border border-mobile-gray-100 bg-white transition-colors duration-200 flex flex-col':
          true,
        'border !border-gray-500':
          isSelected &&
          ((totalClaims != null && totalClaims > 1) || totalClaims == null),
      })}
    >
      <div className="flex justify-between relative">
        {insuranceProviderHandle === 'offline' ? null : (
          <img
            className="h-6"
            alt={insuranceProviderHandle}
            src={INSURER_LOGOS.get(insuranceProviderHandle)}
          />
        )}
        {isSelectable ? (
          <>
            <Radio isChecked={isSelected} />
            <input
              id={rest.id}
              name={rest.name}
              className="invisible absolute w-[1px] h-[1px]"
              type="radio"
              {...rest}
            />
          </>
        ) : null}
      </div>
      <div className="mt-4 flex flex-row gap-2 items-start">
        {Icon && <Icon className="w-8 h-8 my-[6px] flex-shrink-0" />}
        <div className="flex flex-col gap-1">
          <p className="text-mobile-text-main font-bold md:text-xl leading-[140%] font-chromatica">
            {fullName}
          </p>
          <p className="text-mobile-text-hierarchy-1 text-sm leading-[140%] font-medium">
            {CARE_TYPE_CONFIG.get(typeOfCare)?.title}
          </p>
          {insurerClaimId ? (
            <p className="text-mobile-text-hierarchy-1 text-sm leading-[140%] font-medium">
              Claim ID: {insurerClaimId}
            </p>
          ) : null}
          {plumClaimId ? (
            <p className="text-mobile-text-hierarchy-1 text-sm leading-[140%] font-medium">
              Plum Claim ID: {plumClaimId}
            </p>
          ) : null}
        </div>
      </div>
      <div className="flex flex-col">
        <div className="h-[1px] w-full border-t border-dashed border-mobile-gray-200 my-4" />
        <h4 className="text-mobile-text-main text-sm font-medium leading-[140%] md:text-base">
          {stage?.split('-').map(capitalize).join(' ')}
        </h4>
        <p className="text-sm md:text-base font-medium text-mobile-text-hierarchy-1 leading-[140%]">
          Date of admission:{' '}
          {dateOfAdmission
            ? `${format(dateOfAdmission, 'dd MMM yyyy')}`
            : 'Not available'}
        </p>
        <div className="h-[1px] w-full border-t border-dashed border-mobile-gray-200 my-4" />
        <div className="flex flex-row gap-2 items-center">
          <Hospital
            style={{
              flex: '0 0 auto',
            }}
          />
          <p className="text-sm md:text-base font-medium text-mobile-text-main leading-[140%]">
            {(hospitalname?.trim?.()?.length ?? 0) > 0
              ? hospitalname
              : 'No hospital selected'}
          </p>
        </div>
      </div>
    </div>
  );

  if (isSelectable) {
    return (
      <label htmlFor={rest.id} className="cursor-pointer">
        {card}
      </label>
    );
  }

  return card;
};

export default ImportedClaimsCard;
